/**
 * Portal jobs list (tracker item 45): upcoming and past visits at the
 * customer's own properties. Every row shows its property address; there are
 * no prices. The property filter is hidden for a single property.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { PortalJob, PortalPage } from '../../api/types';
import { usePortal, usePortalApi } from './PortalContext';
import { PropertyFilter, usePropertyFilter } from './PropertyFilter';
import { EmptyBlock, FailureBlock, LoadingBlock } from './PortalStates';
import {
  describePortalFailure,
  localToday,
  ownedProperties,
  pastJobs,
  PORTAL_JOB_STATUS_LABELS,
  portalAddress,
  portalDay,
  portalJobStatusClass,
  PortalFailure,
  portalWindow,
  propertyIdParam,
  upcomingJobs,
} from '../../utils/portal';
import { secondaryButtonClass } from '../../components/formStyles';

const PAGE_SIZE = 20;

export function PortalJobRow({ job }: { job: PortalJob }) {
  return (
    <li>
      <Link
        to={`/portal/jobs/${job.id}`}
        className="block min-h-[44px] rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200 hover:ring-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <div className="flex items-start justify-between gap-3">
          <span className="text-sm font-semibold text-gray-900">{portalDay(job.scheduledDate)}</span>
          <span className={`rounded px-2 py-0.5 text-xs font-semibold ${portalJobStatusClass(job.status)}`}>{PORTAL_JOB_STATUS_LABELS[job.status]}</span>
        </div>
        <div className="text-sm text-gray-700">{portalWindow(job)}</div>
        <div className="mt-1 text-base font-semibold text-gray-900" data-testid="job-address">{portalAddress(job.property)}</div>
        {job.items.length > 0 && <div className="mt-1 text-xs text-gray-600">{job.items.map((i) => i.name).join(', ')}</div>}
      </Link>
    </li>
  );
}

export function PortalJobsPage() {
  const { me, reportFailure } = usePortal();
  const api = usePortalApi();
  const choices = ownedProperties(me.properties);
  const [filter, setFilter] = usePropertyFilter(me.user.id, choices);
  const [rows, setRows] = useState<PortalJob[]>([]);
  const [page, setPage] = useState<PortalPage<PortalJob>['pagination'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [moreLoading, setMoreLoading] = useState(false);
  const [failure, setFailure] = useState<PortalFailure | null>(null);
  const requestId = useRef(0);
  const today = localToday();

  const load = useCallback(async (pageNo: number) => {
    const id = ++requestId.current;
    if (pageNo === 1) { setLoading(true); setRows([]); } else setMoreLoading(true);
    setFailure(null);
    try {
      const result = await api.listPortalJobs({ propertyId: propertyIdParam(filter), page: pageNo, limit: PAGE_SIZE });
      if (id !== requestId.current) return;
      setRows((current) => (pageNo === 1 ? result.data : [...current, ...result.data]));
      setPage(result.pagination);
    } catch (error) {
      if (id !== requestId.current) return;
      const described = describePortalFailure(error, 'We could not load your visits.');
      setFailure(described);
      reportFailure(described);
    } finally {
      if (id === requestId.current) { setLoading(false); setMoreLoading(false); }
    }
  }, [api, filter, reportFailure]);

  useEffect(() => { void load(1); }, [load]);

  const upcoming = upcomingJobs(rows, today);
  const past = pastJobs(rows, today);
  const hasMore = !!page && page.page < page.pages;

  return (
    <div>
      <h1 className="mb-3 text-lg font-bold text-gray-900">Your visits</h1>
      <PropertyFilter choices={choices} value={filter} onChange={setFilter} />
      {loading && <LoadingBlock label="Loading visits" />}
      {failure && <FailureBlock failure={failure} onRetry={() => void load(page && rows.length ? page.page + 1 : 1)} />}
      {!loading && !failure && rows.length === 0 && <EmptyBlock>You have no visits yet.</EmptyBlock>}
      {upcoming.length > 0 && (
        <section aria-labelledby="upcoming-heading" className="mb-6">
          <h2 id="upcoming-heading" className="mb-2 text-base font-bold text-gray-900">Upcoming</h2>
          <ul className="space-y-3" aria-label="Upcoming visits">{upcoming.map((job) => <PortalJobRow key={job.id} job={job} />)}</ul>
        </section>
      )}
      {past.length > 0 && (
        <section aria-labelledby="past-heading">
          <h2 id="past-heading" className="mb-2 text-base font-bold text-gray-900">Past</h2>
          <ul className="space-y-3" aria-label="Past visits">{past.map((job) => <PortalJobRow key={job.id} job={job} />)}</ul>
        </section>
      )}
      {hasMore && !failure && (
        <button type="button" className={`${secondaryButtonClass} mt-4 w-full`} disabled={moreLoading} onClick={() => void load((page?.page ?? 1) + 1)}>
          {moreLoading ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  );
}
